/**
 * TacticalRuleCard — single rule rendered as a card in the rule grid.
 *
 * Phase C Workstream 3 §3.2 — DO rules render with a green left
 * accent + green status badge, DON'T rules with rose. Confidence
 * meter, episode-count badge, PDO concept chips, optional perf
 * delta, and admin-action buttons (validate, archive). Click anywhere
 * on the card body opens the detail drawer.
 */

import { clsx } from 'clsx';
import {
  CheckCircle2,
  XCircle,
  Archive,
  ShieldCheck,
  TrendingUp,
} from 'lucide-react';
import { type CreativeMemoryRule } from '@/services/api';
import { ConfidenceMeter } from './ConfidenceMeter';
import { PdoConceptChips } from './PdoConceptChips';

interface TacticalRuleCardProps {
  rule: CreativeMemoryRule;
  /** Show admin buttons (archive / validate) */
  showAdminActions?: boolean;
  /** Disable buttons during in-flight admin actions for this rule */
  pending?: boolean;
  onClick?: (ruleId: string) => void;
  onArchive?: (ruleId: string) => void;
  onValidate?: (ruleId: string) => void;
}

export function TacticalRuleCard({
  rule,
  showAdminActions = true,
  pending = false,
  onClick,
  onArchive,
  onValidate,
}: TacticalRuleCardProps) {
  const isDo = rule.polarity === 'DO';
  const accent = isDo ? 'border-l-emerald-400' : 'border-l-rose-400';
  const badgeStyle = isDo
    ? 'bg-emerald-50 text-emerald-700'
    : 'bg-rose-50 text-rose-700';
  const Icon = isDo ? CheckCircle2 : XCircle;
  const archived = rule.status === 'archived';

  return (
    <article
      className={clsx(
        'rounded-lg border border-l-4 border-slate-200 bg-white p-4 transition',
        accent,
        archived && 'opacity-60',
        onClick && 'cursor-pointer hover:border-slate-300 hover:shadow-soft',
      )}
      onClick={onClick ? () => onClick(rule.rule_id) : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <header className="flex items-start gap-2">
        <div className={clsx('mt-0.5 rounded-md p-1', badgeStyle)}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-slate-800 leading-tight">
            {rule.name}
          </h4>
          {rule.summary && (
            <p className="mt-0.5 text-xs text-slate-600 leading-snug">
              {rule.summary}
            </p>
          )}
        </div>
        <span
          className={clsx(
            'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide shrink-0',
            badgeStyle,
          )}
        >
          {rule.polarity}
        </span>
      </header>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-slate-600">
        <ConfidenceMeter value={rule.confidence} size="sm" />
        <span className="rounded bg-slate-100 px-1.5 py-0.5 tabular-nums text-[10px] text-slate-600">
          {rule.episode_count} eps
        </span>
        {typeof rule.performance_delta_sigma === 'number' &&
          rule.performance_delta_sigma !== 0 && (
            <span
              className={clsx(
                'flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium',
                rule.performance_delta_sigma > 0
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-rose-50 text-rose-700',
              )}
            >
              <TrendingUp className="w-3 h-3" />
              {rule.performance_delta_sigma > 0 ? '+' : ''}
              {rule.performance_delta_sigma.toFixed(1)}σ
            </span>
          )}
        {archived && (
          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
            archived
          </span>
        )}
      </div>

      {rule.pdo_concept_refs.length > 0 && (
        <div className="mt-3">
          <PdoConceptChips
            conceptRefs={rule.pdo_concept_refs}
            conceptIds={rule.pdo_concept_ids}
            size="sm"
          />
        </div>
      )}

      {showAdminActions && !archived && (onArchive || onValidate) && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          {onValidate && (
            <button
              type="button"
              disabled={pending}
              onClick={(e) => {
                e.stopPropagation();
                onValidate(rule.rule_id);
              }}
              className="flex items-center gap-1 rounded-md bg-brand-teal/10 px-2 py-1 text-[11px] font-medium text-brand-teal hover:bg-brand-teal/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ShieldCheck className="w-3 h-3" />
              Validate
            </button>
          )}
          {onArchive && (
            <button
              type="button"
              disabled={pending}
              onClick={(e) => {
                e.stopPropagation();
                onArchive(rule.rule_id);
              }}
              className="flex items-center gap-1 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Archive className="w-3 h-3" />
              Archive
            </button>
          )}
          {pending && (
            <span className="text-[10px] text-slate-400 italic">
              applying…
            </span>
          )}
        </div>
      )}
    </article>
  );
}
